import { Fragment, useCallback, useEffect } from 'react';
import { Mosaic, MosaicBranch } from 'react-mosaic-component';
import { useNavigate } from 'react-router';
import Layout from './components/layout/Layout';
import IdleModal from './components/modal/IdleModal';
import ChartModule from './components/modules/Chart/ChartModule';
import OrderBookModule from './components/modules/OrderBook/OrderBookModule';
import FeatureModule from './components/mosaic/FeatureModule';
import BlottersModule from './components/trader-modules/Blotters/BlottersModule';
import TickersModule from './components/trader-modules/Tickers/TickersModule';
import { addMarketFull } from './services/MarketService';
import { useAppDispatch, useAppSelector } from './state/hooks';
import { controlInitSubscriptions, selectCredentials } from './state/reducers/authSlice';
import { getBlotterItemsThunk, getBlotterTradesThunk } from './state/reducers/blotterSlice';
import { fetchUserMarkets, PairMap, setActivePair } from './state/reducers/marketPairSlice';
import { setIsResizing } from './state/reducers/mosaicSlice';
import { selectGeneralSettings } from './state/reducers/settingSlice';
import {
    setXplorTraderDesktopLayoutNode,
    setXplorTraderLaptopLayoutNode
} from './state/reducers/xplorTraderLayoutSlice';
import { store } from './state/store';
import { useDisclosure } from './utils/hooks/useDisclosure';
import { useIdle } from './utils/hooks/useIdle';
import useScreenSizes from './utils/hooks/useScreenSizes';
import useXplorTraderLayout, { XplorTraderModules } from './utils/hooks/useXplorTraderLayout';
import { navigateAwayFromXplorTrader } from './utils/navigation';
import { isCorrectLayout } from './utils/route';

const xplortraderEnabled = window.config?.layout?.xplortrader?.enabled;

export const initActivePair = () => {
    const state = store.getState();
    const dispatch = store.dispatch;

    const currentPair = state.marketPair.activePair?.netdania;
    const pairs = state.marketPair.pairs;
    if (!currentPair || !pairs.find((pair) => pair.netdania === currentPair)) {
        const primary = pairs.find((pair) => pair.netdania === 'BTC/USD');
        const secondary = pairs.find((pair) => pair.netdania === 'EUR/USD');
        const activePair = primary || secondary || pairs[0];
        dispatch(setActivePair(activePair));
        return activePair;
    } else {
        dispatch(setActivePair(state.marketPair.activePair as PairMap));
        return state.marketPair.activePair;
    }
};

function XplorTrader({ prevPath }: { prevPath: string | null }) {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const settings = useAppSelector(selectGeneralSettings);
    const credentials = useAppSelector(selectCredentials);

    const idle = useIdle(settings.idleTimeout.value);
    const idleDisclosure = useDisclosure(false);

    const screen = useScreenSizes();
    const { layout, default_layout, config } = useXplorTraderLayout(screen);

    const renderTile = useCallback(
        (id: XplorTraderModules, path: MosaicBranch[]) => {
            const props = {
                path,
                screen,
                tradingModule: config[id],
                orderBookModule: config[id]
            };
            switch (id) {
                case 'Tickers':
                    return <TickersModule {...props} />;
                case 'Blotter':
                    return <BlottersModule {...props} />;
                case 'Chart':
                    return <ChartModule {...props} />;
                case 'Order Book':
                    return <OrderBookModule {...props} />;
                default:
                    return <FeatureModule {...props} />;
            }
        },
        [screen, config]
    );

    useEffect(() => {
        if (idle) idleDisclosure[1].open();
    }, [idle]);

    useEffect(() => {
        if (!credentials) {
            navigate('/login');
        } else {
            if (xplortraderEnabled && isCorrectLayout('xplortrader')) {
                // Fire initial requests
                dispatch(controlInitSubscriptions());
                dispatch(fetchUserMarkets()).then(() => {
                    const activePair = initActivePair() as PairMap;
                    addMarketFull(activePair?.celer);
                    dispatch(getBlotterItemsThunk()).then(() => dispatch(getBlotterTradesThunk()));
                });
            } else {
                navigateAwayFromXplorTrader(navigate);
            }
        }
    }, []);

    return credentials ? (
        <Fragment>
            <Layout prevPath={prevPath}>
                <Mosaic<XplorTraderModules>
                    renderTile={renderTile}
                    value={layout}
                    initialValue={layout || default_layout}
                    onChange={(node) => {
                        if (screen.laptop) dispatch(setXplorTraderLaptopLayoutNode(node));
                        if (screen.desktop) dispatch(setXplorTraderDesktopLayoutNode(node));
                        dispatch(setIsResizing(true));
                    }}
                    onRelease={() => dispatch(setIsResizing(false))}
                    resize={{ minimumPaneSizePercentage: 5 }}
                />
                <IdleModal opened={idleDisclosure[0]} handlers={idleDisclosure[1]} />
            </Layout>
        </Fragment>
    ) : null;
}

export default XplorTrader;
